import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

// Cart item types that can be added to cart
export interface CartCourse {
  type: 'course';
  id: string;
  title: string;
  slug: string;
  priceUSD: string;
  posterUrl?: string;
  description?: string;
}

export interface CartBooking {
  type: 'booking';
  id: string;
  consultantName: string;
  serviceType: string;
  startTime: string;
  endTime: string;
  priceUSD: string;
  duration: string;
}

export interface CartMembership {
  type: 'membership';
  id: string;
  name: string;
  priceUSD: string;
  billingInterval: string;
  description?: string;
}

export interface CartShopItem {
  type: 'shop';
  id: string; // Printify product ID
  variantId: number; // Printify variant ID
  title: string;
  variantTitle: string;
  priceUSD: string;
  quantity: number;
  image?: string;
  sku?: string;
}

export type CartItemData = CartCourse | CartBooking | CartMembership | CartShopItem;

// Type-safe metadata types for server sync
export interface CourseMetadata {
  title: string;
  slug: string;
  posterUrl?: string;
  description?: string;
}

export interface BookingMetadata {
  consultantName: string;
  serviceType: string;
  startTime: string;
  endTime: string;
  duration: string;
}

export interface MembershipMetadata {
  name: string;
  billingInterval: string;
  description?: string;
}

export interface ShopItemMetadata {
  title: string;
  variantId: number;
  variantTitle: string;
  quantity: number;
  image?: string;
  sku?: string;
}

export type CartItemMetadata =
  | CourseMetadata
  | BookingMetadata
  | MembershipMetadata
  | ShopItemMetadata;

// Build type-safe metadata from cart item
function buildCartItemMetadata(item: CartItemData): CartItemMetadata {
  switch (item.type) {
    case 'course':
      return {
        title: item.title,
        slug: item.slug,
        posterUrl: item.posterUrl,
        description: item.description,
      };
    case 'booking':
      return {
        consultantName: item.consultantName,
        serviceType: item.serviceType,
        startTime: item.startTime,
        endTime: item.endTime,
        duration: item.duration,
      };
    case 'membership':
      return {
        name: item.name,
        billingInterval: item.billingInterval,
        description: item.description,
      };
    case 'shop':
      return {
        title: item.title,
        variantId: item.variantId,
        variantTitle: item.variantTitle,
        quantity: item.quantity,
        image: item.image,
        sku: item.sku,
      };
  }
}

// Helper to check if cart has physical shop items (requires shipping)
export function cartHasPhysicalShopItems(items: CartItemData[]): boolean {
  return items.some(item => item.type === 'shop');
}

// Helper to get total cart item count (quantity-aware for shop items)
export function getCartItemCount(items: CartItemData[]): number {
  return items.reduce((sum, item) => {
    if (item.type === 'shop') {
      return sum + (item as CartShopItem).quantity;
    }
    return sum + 1;
  }, 0);
}

// Helper to check if cart has subscriptions
export function cartHasSubscriptions(items: CartItemData[]): boolean {
  return items.some(item => item.type === 'membership');
}

// Helper to split cart into digital and physical items
export function splitCartByType(items: CartItemData[]): {
  digitalItems: CartItemData[];
  physicalItems: CartShopItem[];
} {
  const digitalItems: CartItemData[] = [];
  const physicalItems: CartShopItem[] = [];

  for (const item of items) {
    if (item.type === 'shop') {
      physicalItems.push(item);
    } else {
      digitalItems.push(item);
    }
  }

  return { digitalItems, physicalItems };
}

export interface CartState {
  items: CartItemData[];
  isLoading: boolean;
  total: number;
}

// Properly typed action payloads for shop items with variantId
interface RemoveItemPayload {
  type: string;
  id: string;
  variantId?: number;
}

interface UpdateQuantityPayload {
  type: string;
  id: string;
  variantId?: number;
  quantity: number;
}

type CartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ITEMS'; payload: CartItemData[] }
  | { type: 'ADD_ITEM'; payload: CartItemData }
  | { type: 'REMOVE_ITEM'; payload: RemoveItemPayload }
  | { type: 'UPDATE_QUANTITY'; payload: UpdateQuantityPayload }
  | { type: 'CLEAR_CART' };

// Calculate total considering shop item quantity
function calculateTotal(items: CartItemData[]): number {
  return items.reduce((sum, item) => {
    if (item.type === 'shop') {
      return sum + parseFloat(item.priceUSD) * item.quantity;
    }
    return sum + parseFloat(item.priceUSD);
  }, 0);
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_LOADING': {
      return { ...state, isLoading: action.payload };
    }

    case 'SET_ITEMS': {
      const total = calculateTotal(action.payload);
      return { ...state, items: action.payload, total };
    }

    case 'ADD_ITEM': {
      // For shop items, check by product ID AND variant ID
      if (action.payload.type === 'shop') {
        const shopPayload = action.payload as CartShopItem;
        const existingShopIndex = state.items.findIndex(
          item =>
            item.type === 'shop' &&
            item.id === shopPayload.id &&
            (item as CartShopItem).variantId === shopPayload.variantId,
        );

        if (existingShopIndex >= 0) {
          // Increase quantity of existing shop item
          const updatedItems = [...state.items];
          const existingShop = updatedItems[existingShopIndex] as CartShopItem;
          updatedItems[existingShopIndex] = {
            ...existingShop,
            quantity: existingShop.quantity + shopPayload.quantity,
          };
          return { ...state, items: updatedItems, total: calculateTotal(updatedItems) };
        }
      } else {
        // Check if item already exists for non-shop items
        const existingItemIndex = state.items.findIndex(
          item => item.type === action.payload.type && item.id === action.payload.id,
        );

        if (existingItemIndex >= 0 && (action.payload.type === 'course' || action.payload.type === 'membership')) {
              return state;
        }
      }

      const newItems = [...state.items, action.payload];
      return { ...state, items: newItems, total: calculateTotal(newItems) };
    }

    case 'REMOVE_ITEM': {
      const { type: itemType, id, variantId } = action.payload;
      let filteredItems: CartItemData[];

      // For shop items, also check variantId if provided
      if (itemType === 'shop' && variantId !== undefined) {
        filteredItems = state.items.filter(item => {
          if (item.type !== 'shop') return true;
          const shopItem = item as CartShopItem;
          return !(shopItem.id === id && shopItem.variantId === variantId);
        });
      } else {
        filteredItems = state.items.filter(item => !(item.type === itemType && item.id === id));
      }

      return { ...state, items: filteredItems, total: calculateTotal(filteredItems) };
    }

    case 'UPDATE_QUANTITY': {
      const { type: itemType, id, variantId, quantity } = action.payload;
      // Handle shop item quantity updates
      if (itemType === 'shop' && variantId !== undefined) {
        const updatedItems = state.items.map(item => {
          if (item.type !== 'shop') return item;
          const shopItem = item as CartShopItem;
          if (shopItem.id === id && shopItem.variantId === variantId) {
            return { ...shopItem, quantity };
          }
          return item;
        });
        return { ...state, items: updatedItems, total: calculateTotal(updatedItems) };
      }
      // For bookings/courses/memberships, quantity is always 1
      return state;
    }

    case 'CLEAR_CART': {
      return { ...state, items: [], total: 0 };
    }

    default:
      return state;
  }
};

interface CartContextType {
  state: CartState;
  addToCart: (_item: CartItemData) => Promise<void>;
  removeFromCart: (_type: string, _id: string, _variantId?: number) => Promise<void>;
  updateShopItemQuantity: (_productId: string, _variantId: number, _quantity: number) => void;
  clearCart: () => Promise<void>;
  isInCart: (_type: string, _id: string, _variantId?: number) => boolean;
  getShopItemQuantity: (_productId: string, _variantId: number) => number;
  syncWithServer: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    isLoading: false,
    total: 0,
  });

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartItems = JSON.parse(savedCart) as CartItemData[];

        // Validate and sanitize cart items
        const validItems = cartItems.filter(item => {
          // Check required fields exist
          if (!item.type || !item.id || !item.priceUSD) {
            console.warn('[CartContext] (NO $) Invalid cart item removed:', item);
            return false;
          }

          // For shop items, ensure numeric fields are numbers
          if (item.type === 'shop') {
            const shopItem = item as CartShopItem;
            // Coerce to numbers if they're strings
            if (typeof shopItem.variantId === 'string') {
              shopItem.variantId = parseInt(shopItem.variantId, 10);
            }
            if (typeof shopItem.quantity === 'string') {
              shopItem.quantity = parseInt(shopItem.quantity, 10);
            }
            // Validate
            if (isNaN(shopItem.variantId) || isNaN(shopItem.quantity) || shopItem.quantity < 1) {
              console.warn('[CartContext] (NO $) Invalid shop item removed:', shopItem);
              return false;
            }
          }

          return true;
        });

        dispatch({ type: 'SET_ITEMS', payload: validItems });

        // Update localStorage if any items were filtered out
        if (validItems.length !== cartItems.length) {
          localStorage.setItem('cart', JSON.stringify(validItems));
        }
      } catch (error) {
        console.error('[CartContext] (NO $) Failed to parse saved cart:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  const syncWithServer = useCallback(
    async (retryCount = 0): Promise<void> => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 1000; // 1 second

      if (!isAuthenticated || !user) return;

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Send current cart items to server with type-safe metadata
        await apiRequest('POST', '/api/cart/sync', {
          items: state.items.map(item => ({
            itemType: item.type,
            itemId: item.id,
            priceUSD: item.priceUSD,
            quantity: item.type === 'shop' ? (item as CartShopItem).quantity : 1,
            metadata: buildCartItemMetadata(item),
          })),
        });
      } catch (error) {
        console.error(
          `[CartContext] (NO $) Failed to sync cart with server (attempt ${retryCount + 1}):`,
          error,
        );

        // Retry with exponential backoff
        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY * Math.pow(2, retryCount);
          console.log(`[CartContext] (NO $) Retrying sync in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return syncWithServer(retryCount + 1);
        }

        // After max retries, notify user
        toast({
          title: 'Sync Warning',
          description: 'Cart changes saved locally. Server sync will retry later.',
          variant: 'destructive',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [isAuthenticated, user, state.items, toast],
  );

  // Track previous user ID to detect user changes
  const previousUserIdRef = useRef<string | null>(null);

  // Handle user session changes (logout/login with different user)
  useEffect(() => {
    const currentUserId = user?.id || null;
    const previousUserId = previousUserIdRef.current;

    // User logged out - clear cart
    if (previousUserId && !currentUserId) {
      console.log('[CartContext] (NO $) User logged out, clearing cart');
      dispatch({ type: 'CLEAR_CART' });
      localStorage.removeItem('cart');
    }
    // Different user logged in - clear previous user's cart
    else if (currentUserId && previousUserId && currentUserId !== previousUserId) {
      console.log('[CartContext] (NO $) Different user logged in, clearing previous cart');
      dispatch({ type: 'CLEAR_CART' });
      localStorage.removeItem('cart');
    }

    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  // Sync with server when user authenticates
  useEffect(() => {
    if (isAuthenticated && user && state.items.length > 0) {
      syncWithServer();
    }
  }, [isAuthenticated, user, state.items.length, syncWithServer]);

  const addToCart = useCallback(
    async (item: CartItemData) => {
      // Store previous state for rollback
      const previousItems = state.items;

      // Optimistic update
      dispatch({ type: 'ADD_ITEM', payload: item });

      // Build toast description based on item type
      let description = '';
      if (item.type === 'course') {
        description = `${item.title} added to your cart.`;
      } else if (item.type === 'booking') {
        description = `${item.serviceType} session added to your cart.`;
      } else if (item.type === 'membership') {
        description = `${item.name} added to your cart.`;
      } else if (item.type === 'shop') {
        description = `${item.title} - ${item.variantTitle} added to your cart.`;
      }

      toast({
        title: 'Added to Cart',
        description,
      });

      // Sync with server if authenticated
      if (isAuthenticated && user) {
        try {
          // Build type-safe metadata based on item type
          const metadata = buildCartItemMetadata(item);

          await apiRequest('POST', '/api/cart/add', {
            itemType: item.type,
            itemId: item.id,
            priceUSD: item.priceUSD,
            metadata,
          });
        } catch (error) {
          // Rollback on failure
          console.error(
            '[CartContext] (NO $) Failed to add item to server cart, rolling back:',
            error,
          );
          dispatch({ type: 'SET_ITEMS', payload: previousItems });
          toast({
            title: 'Error',
            description: 'Failed to add item to cart. Please try again.',
            variant: 'destructive',
          });
          // Re-throw so callers know the operation failed
          throw error;
        }
      }
    },
    [isAuthenticated, user, toast, state.items],
  );

  const removeFromCart = useCallback(
    async (type: string, id: string, variantId?: number) => {
      // Store previous state for rollback
      const previousItems = state.items;

      // Optimistic update
      dispatch({ type: 'REMOVE_ITEM', payload: { type, id, variantId } });

      toast({
        title: 'Removed from Cart',
        description: 'Item removed from your cart.',
      });

      // Sync with server if authenticated
      if (isAuthenticated && user) {
        try {
          const endpoint =
            variantId !== undefined
              ? `/api/cart/remove/${type}/${id}/${variantId}`
              : `/api/cart/remove/${type}/${id}`;
          await apiRequest('DELETE', endpoint);
        } catch (error) {
          // Rollback on failure
          console.error(
            '[CartContext] (NO $) Failed to remove item from server cart, rolling back:',
            error,
          );
          dispatch({ type: 'SET_ITEMS', payload: previousItems });
          toast({
            title: 'Error',
            description: 'Failed to remove item. Please try again.',
            variant: 'destructive',
          });
        }
      }
    },
    [isAuthenticated, user, toast, state.items],
  );

  const updateShopItemQuantity = useCallback(
    (productId: string, variantId: number, quantity: number) => {
      if (quantity < 1) {
        removeFromCart('shop', productId, variantId);
        return;
      }

      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: { type: 'shop', id: productId, variantId, quantity },
      });
    },
    [removeFromCart],
  );

  const getShopItemQuantity = useCallback(
    (productId: string, variantId: number): number => {
      const item = state.items.find(
        i => i.type === 'shop' && i.id === productId && (i as CartShopItem).variantId === variantId,
      );
      return item ? (item as CartShopItem).quantity : 0;
    },
    [state.items],
  );

  const clearCart = useCallback(async () => {
    dispatch({ type: 'CLEAR_CART' });

    toast({
      title: 'Cart Cleared',
      description: 'All items removed from your cart.',
    });

    // Clear server cart if authenticated
    if (isAuthenticated && user) {
      try {
        await apiRequest('DELETE', '/api/cart/clear');
      } catch (error) {
        console.error('Failed to clear server cart:', error);
      }
    }
  }, [isAuthenticated, user, toast]);

  const isInCart = useCallback(
    (type: string, id: string, variantId?: number) => {
      if (type === 'shop' && variantId !== undefined) {
        return state.items.some(
          item =>
            item.type === 'shop' &&
            item.id === id &&
            (item as CartShopItem).variantId === variantId,
        );
      }
      return state.items.some(item => item.type === type && item.id === id);
    },
    [state.items],
  );

  return (
    <CartContext.Provider
      value={{
        state,
        addToCart,
        removeFromCart,
        updateShopItemQuantity,
        clearCart,
        isInCart,
        getShopItemQuantity,
        syncWithServer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
